import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';

import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ConfigService } from '../../shared-services/config.service';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '../../shared-services/environment-config.token';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildLineAreaOption } from './unified-line-area-option.builder';
import type { LineAreaApiConfig, LineAreaWidgetData } from './unified-line-area-chart.types';

/**
 * Unified Line & Area chart — ONE ECharts component for the whole family
 * (Simple / Multi / Step / Spline lines; Simple / Stacked / 100% / Spline areas)
 * plus dual axes and analytics overlays (Trend / Forecast / Moving Average).
 *
 * All variation lives in `data.capabilities` (see LineAreaCapabilities); the
 * PURE `buildLineAreaOption` turns capabilities + flat rows into the option and
 * this component is just the lifecycle shell (init / dispose / resize / theme),
 * mirroring the Bar & Column family. Reads pre-fetched rows from
 * `staticData` / `rows` / `data` or self-fetches via ConfigService.getConfig.
 */
@Component({
  selector: 'app-unified-line-area-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './unified-line-area-chart.component.html',
  styleUrl: './unified-line-area-chart.component.scss',
})
export class UnifiedLineAreaChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: LineAreaWidgetData | Record<string, unknown>;

  /** Emitted on point click when `capabilities.emitSelection` is on. */
  @Output() selection = new EventEmitter<{ category: string; series?: string; value: number }>();

  /** Emitted on point click when `capabilities.crossFilter` is on — the column +
   *  the FULL set of currently-selected values (multi-select toggle). */
  @Output() crossFilterChange = new EventEmitter<{ column: string; value: string | null; values: string[] }>();

  protected myChart!: echarts.ECharts;
  private highlighted = new Set<string>();
  private crossFilterValues = new Set<string>();

  @ViewChild('ulaHost', { static: false }) private hostRef?: ElementRef<HTMLDivElement>;

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private environmentConfig = inject(ENVIRONMENT_CONFIG, { optional: true }) as EnvironmentConfig | null;
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private lastRows: Array<Record<string, unknown>> = [];
  /** Render width used to keep dense category charts readable with scrolling. */
  protected chartWidthPx: number | null = null;
  /** Host width the current option was built at — see the ResizeObserver. */
  private lastBuiltWidth = 0;

  private get cfg(): LineAreaWidgetData {
    return (this.data || {}) as LineAreaWidgetData;
  }

  get hostHeight(): string {
    const h = this.cfg.height;
    if (h === undefined || h === null || h === '') return 'var(--dvm-chart-height, 300px)';
    if (typeof h === 'number') return `${h}px`;
    return /^\d+$/.test(String(h)) ? `${h}px` : String(h);
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => [],
    });

    const staticRows = this.hasApiOverride() ? null : this.resolveStaticRows();
    if (staticRows) {
      setTimeout(() => this.renderRows(staticRows), 0);
    } else {
      setTimeout(() => this.loadAndRenderChart(initialFilters ?? undefined), 0);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    const host = this.resolveHost();
    if (host && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateChartWidth(this.lastRows);
        this.myChart?.resize();
        // The category-label step is baked into the option at build time, so a
        // width change has to REBUILD or the axis keeps a density picked for the
        // old size. Only on a material change — resize() alone handles the rest.
        const w = this.myChart?.getWidth() || 0;
        if (w && this.lastBuiltWidth && Math.abs(w - this.lastBuiltWidth) / this.lastBuiltWidth > 0.15) {
          this.renderRows(this.lastRows);
        }
      });
      this.resizeObserver.observe(host);
    }

    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.myChart || !this.lastRows.length) return;
      const chartDom = this.resolveHost();
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderRows(this.lastRows);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || changes['data'].isFirstChange()) return;
    if (!this.myChart) return;
    if (this.hasApiOverride()) {
      // API-backed charts are fetched by the wrapper's filter subscription.
      // The parent can replace the widget object after that request starts;
      // fetching here as well sends the default payload without the filter
      // and can overwrite the correctly filtered response.
      return;
    }
    const staticRows = this.resolveStaticRows();
    this.renderRows(staticRows ?? this.lastRows);
  }

  private resolveHost(): HTMLElement | null {
    return this.hostRef?.nativeElement || document.getElementById(this.cfg.widgetId || '');
  }

  /** An explicitly configured API must win over cached preview/static rows. */
  private hasApiOverride(): boolean {
    const apiOverride = this.cfg.apiOverride || this.cfg.capabilities?.apiOverride;
    return !!String(apiOverride?.url || '').trim();
  }

  private resolveStaticRows(): Array<Record<string, unknown>> | null {
    const candidate = this.cfg.staticData ?? this.cfg.rows ?? this.cfg.data;
    if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    const nested = (candidate as { data?: unknown })?.data;
    return Array.isArray(nested) ? (nested as Array<Record<string, unknown>>) : null;
  }

  private updateChartWidth(rows: Array<Record<string, unknown>>): void {
    const host = this.resolveHost();
    const viewportWidth = host?.parentElement?.clientWidth || host?.clientWidth || 0;
    const categoryField = this.cfg.capabilities?.category;
    const categoryCount = categoryField
      ? new Set(rows.map((row) => String(row?.[categoryField] ?? ''))).size
      : 0;
    const rotation = Number(this.cfg.capabilities?.axisLabelRotation);
    // Rotated labels need only a narrow, stable cell. Unrotated labels need
    // more room so their text and the secondary table cells do not collide.
    const widthPerCategory = Number.isFinite(rotation) && Math.abs(rotation) >= 60 ? 34 : 88;
    const requiredWidth = Math.max(320, categoryCount * widthPerCategory + 72);
    this.chartWidthPx = Math.max(viewportWidth, requiredWidth);
  }

  protected loadAndRenderChart(filters?: unknown): void {
    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    const staticRows = this.hasApiOverride() ? null : this.resolveStaticRows();
    if (staticRows) {
      this.renderRows(staticRows);
      return;
    }
    const apiOverride = this.cfg.apiOverride || this.cfg.capabilities?.apiOverride;
    const apiUrl = this.resolveLineAreaApiUrl(String(apiOverride?.url || this.cfg.apiUrl || '').trim());
    if (!apiUrl) {
      this.renderRows([]);
      return;
    }

    this.myChart.showLoading();
    const requestConfig = apiOverride
      ? this.buildApiOverrideRequestConfig(apiOverride)
      : this.data;
    // Keep the global/filter-wrapper state for alternate APIs too. ConfigService
    // merges filters.requestParams into the configured requestData, so every
    // reload sends the current dynamic selections to the microservice.
    this.configService.getConfig(apiUrl, requestConfig, filters,
      apiOverride?.method || undefined)?.subscribe({
      next: (res: unknown) => {
        this.renderRows(this.normalizeApiRows(this.extractApiRows(res, apiOverride)));
        this.myChart.hideLoading();
      },
      error: () => this.myChart.hideLoading(),
    });
  }

  /** Resolve relative chart API paths against the host app's environment base,
   * matching the table component. Absolute URLs remain unchanged. */
  private resolveLineAreaApiUrl(configuredUrl: string): string {
    if (!configuredUrl || /^https?:\/\//i.test(configuredUrl)) return configuredUrl;
    const envBase = String(this.environmentConfig?.SELF_SERVICE_API_BASE_URL || '').trim().replace(/\/$/, '');
    if (!envBase) return configuredUrl;
    return `${envBase}${configuredUrl.startsWith('/') ? configuredUrl : `/${configuredUrl}`}`;
  }

  /** Send an explicitly configured request body unchanged. This supports the
   * platform envelope supplied by the user as well as a simple custom body. */
  private buildApiOverrideRequestConfig(api: LineAreaApiConfig): Record<string, unknown> {
    const configuredPayload = api.payload && typeof api.payload === 'object' && !Array.isArray(api.payload)
      ? { ...(api.payload as Record<string, unknown>) }
      : {};
    // The alternate API owns its envelope. Dataset metadata is used by the
    // dynamic compute API, but must not be added to this custom microservice
    // payload unless the user explicitly included it in the payload editor.
    const payload = configuredPayload;
    return {
      ...(this.data as Record<string, unknown>),
      apiUrl: this.resolveLineAreaApiUrl(api.url),
      method: api.method || 'post',
      customPayload: payload,
      skipDefaultPayload: true,
      scalarizeSingleFilterValues: true,
      preserveResponseShape: true,
    };
  }

  private extractApiRows(response: unknown, api?: LineAreaApiConfig): Array<Record<string, unknown>> {
    const configured = api?.responsePath ? this.readPath(response, api.responsePath) : undefined;
    const candidate = configured !== undefined ? configured : response;
    if (Array.isArray(candidate)) return candidate.filter(this.isRow) as Array<Record<string, unknown>>;
    if (!candidate || typeof candidate !== 'object') return [];

    const body = candidate as Record<string, unknown>;
    const nestedCandidates: unknown[] = [
      body['data'],
      body['rows'],
      body['results'],
      body['responseList'],
      (body['result'] as Record<string, unknown> | undefined)?.['data'],
    ];
    for (const nested of nestedCandidates) {
      if (Array.isArray(nested)) {
        const rows = nested.flatMap((item: unknown) => {
          if (this.isRow(item)) return [item as Record<string, unknown>];
          if (item && typeof item === 'object') {
            const itemBody = item as Record<string, unknown>;
            // Platform responses can wrap the row array under the entity name,
            // for example responseList[0].cus_item_recommendation_supp_bd.
            // Keep the common keys first, then inspect one nested array value.
            const itemRows = itemBody['data']
              ?? itemBody['rows']
              ?? itemBody['results']
              ?? Object.values(itemBody).find((value) => Array.isArray(value));
            return Array.isArray(itemRows) ? itemRows.filter(this.isRow) as Array<Record<string, unknown>> : [];
          }
          return [];
        });
        if (rows.length) return rows;
      }
    }

    // A single row response is also valid for a chart with one category.
    return this.isRow(body) ? [body] : [];
  }

  private readPath(source: unknown, path: string): unknown {
    return String(path).split('.').filter(Boolean).reduce<unknown>((value, key) => {
      return value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined;
    }, source);
  }

  private isRow(value: unknown): value is Record<string, unknown> {
    // A platform responseList item is also an object, but it contains the
    // entity's row array (for example { cus_item_recommendation_supp_bd: [] }).
    // Do not mistake that wrapper for a chart row.
    return !!value
      && typeof value === 'object'
      && !Array.isArray(value)
      && !Object.values(value as Record<string, unknown>).some((entry) => Array.isArray(entry));
  }

  /** Keep API column spelling differences from producing an empty series.
   *  In particular, older configurations used `price_recomended` while the
   *  recommendation service returns `price_recommended`. */
  private normalizeApiRows(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    const fields = [
      this.cfg.capabilities?.category,
      this.cfg.capabilities?.series,
      ...(this.cfg.capabilities?.measures || []).map((measure) => measure.field),
      this.cfg.capabilities?.secondaryXField,
    ].filter((field): field is string => !!field);
    return rows.map((row) => {
      const next = { ...row };
      fields.forEach((field) => {
        if (next[field] !== undefined) return;
        const alias = field.includes('recomended')
          ? field.replace('recomended', 'recommended')
          : field.includes('recommended')
            ? field.replace('recommended', 'recomended')
            : undefined;
        if (alias && row[alias] !== undefined) next[field] = row[alias];
      });
      return next;
    });
  }

  private renderRows(rows: Array<Record<string, unknown>>): void {
    const capabilities = this.cfg.capabilities;
    if (!capabilities) return;
    this.lastRows = rows;
    this.updateChartWidth(rows);

    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    // Width feeds the shared category-label step (labels + secondary-X cells
    // must thin together). getWidth() is 0 before the first layout pass, so
    // fall back to the host box.
    const hostWidthPx = this.myChart.getWidth() || chartDom.clientWidth || chartDom.getBoundingClientRect().width;
    this.lastBuiltWidth = hostWidthPx;
    const option = buildLineAreaOption(rows, capabilities, hostWidthPx) as Record<string, unknown>;
    applyRoleVisibilityToOption(option, this.data);
    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.myChart.resize();
    setTimeout(() => this.myChart?.resize(), 60);
    this.addResizeListener();
    this.wrapper.afterRender();
    this.myChart.off('legendselectchanged');
    this.setupInteractions();
  }

  private setupInteractions(): void {
    const cap = this.cfg.capabilities;
    if (!cap || !this.myChart) return;
    const anyClick = cap.highlightOnClick || cap.crossFilter || cap.emitSelection;
    if (!anyClick) return;
    this.myChart.off('click');

    this.myChart.on('click', (params: { seriesType?: string; name?: string; seriesName?: string; value?: unknown }) => {
      if (params.seriesType !== 'line') return;
      const category = String(params.name ?? '');
      const value = Number(params.value ?? 0);

      if (cap.emitSelection) {
        this.selection.emit({ category, series: cap.series ? params.seriesName : undefined, value });
      }
      if (cap.highlightOnClick) {
        this.toggleHighlight(category);
      }
      if (cap.crossFilter) {
        const column = cap.crossFilterKey || cap.category;
        if (this.crossFilterValues.has(category)) this.crossFilterValues.delete(category);
        else this.crossFilterValues.add(category);
        const values = Array.from(this.crossFilterValues);
        this.applyCrossFilterEmphasis();
        this.crossFilterChange.emit({ column, value: category, values });
      }
    });
  }

  private toggleHighlight(category: string): void {
    if (this.highlighted.has(category)) this.highlighted.delete(category);
    else this.highlighted.add(category);
    this.myChart.dispatchAction({ type: 'downplay' });
    if (this.highlighted.size > 0) {
      const opt = this.myChart.getOption() as { xAxis?: Array<{ data?: string[] }> };
      const axisData = opt.xAxis?.[0]?.data || [];
      axisData.forEach((name, dataIndex) => {
        if (this.highlighted.has(String(name))) {
          this.myChart.dispatchAction({ type: 'highlight', dataIndex });
        }
      });
    }
  }

  private applyCrossFilterEmphasis(): void {
    if (!this.myChart) return;
    this.myChart.dispatchAction({ type: 'downplay' });
    if (this.crossFilterValues.size === 0) return;
    const opt = this.myChart.getOption() as { xAxis?: Array<{ data?: string[] }> };
    const axisData = opt.xAxis?.[0]?.data || [];
    axisData.forEach((name, dataIndex) => {
      if (this.crossFilterValues.has(String(name))) {
        this.myChart.dispatchAction({ type: 'highlight', dataIndex });
      }
    });
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
